import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiBell, FiExternalLink } from 'react-icons/fi';
import { getRestaurantOrders, updateOrderStatus } from '../services/orderService';
import { supabase } from '../services/supabase';
import styles from './OrdersDashboard.module.css';
import Logo from '../components/Logo';

export default function OrdersDashboard() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('new');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newOrderNotification, setNewOrderNotification] = useState(false);
  const audioRef = useRef(null);
  const [stripeAccountId, setStripeAccountId] = useState(null);

  useEffect(() => {
    loadOrders();

    // Set up Supabase realtime subscription
    const subscription = supabase
      .channel('orders-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${restaurantId}`
        },
        (payload) => {
          console.log('Real-time update received:', payload);

          // Play notification sound and show alert for new orders
          if (payload.eventType === 'INSERT') {
            handleNewOrder();
          }

          // Refresh orders when any change happens
          loadOrders();
        }
      )
      .subscribe();

    // Clean up subscription when component unmounts
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [restaurantId, activeTab]);

  const handleNewOrder = () => {
    // Play notification sound
    if (audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }

    // Show notification
    setNewOrderNotification(true);

    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNewOrderNotification(false);
    }, 5000);
  };

  const loadOrders = async () => {
    try {
      const data = await getRestaurantOrders(restaurantId, activeTab !== 'all' ? activeTab : null);
      setOrders(data);

      // If we have at least one order, get the restaurant name
      if (data.length > 0 && data[0].restaurant) {
        setRestaurant(data[0].restaurant);
      } else {
        // If no orders with restaurant info, fetch restaurant separately
        const { data: restaurantData } = await supabase
          .from('restaurants')
          .select('name, stripe_account_id')
          .eq('id', restaurantId)
          .single();

        if (restaurantData) {
          setRestaurant(restaurantData);
          setStripeAccountId(restaurantData.stripe_account_id);
        }
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);

      // Update selected order if it's the one being changed
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }

      // Orders will be refreshed automatically by the realtime subscription
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handleStripeDashboard = () => {
    if (stripeAccountId) {
      window.open(`https://dashboard.stripe.com/${stripeAccountId}`, '_blank');
    }
  };

  const formatDate = (dateString) => {
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#3498db';
      case 'preparing': return '#f39c12';
      case 'ready': return '#2ecc71';
      case 'completed': return '#27ae60';
      case 'cancelled': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  return (
    <div className={styles.container}>
      {/* Audio element for notifications */}
      <audio ref={audioRef} src="/notification-sound.mp3" />

      {/* New Order Notification */}
      {newOrderNotification && (
        <div className={styles.notification}>
          <FiBell size={20} />
          <span>New order received!</span>
          <button onClick={() => setNewOrderNotification(false)}>
            Dismiss
          </button>
        </div>
      )}

      <div className={styles.content}>
        {/* Page header with back button and Stripe button */}
        <div className={styles.pageHeader}>
          <h1>{restaurant?.name || 'Order Management'}</h1>
          <div className={styles.pageHeaderButtons}>
            {stripeAccountId && (
              <button
                onClick={handleStripeDashboard}
                className={styles.secondaryButton}
              >
                <FiExternalLink size={16} /> Stripe Dashboard
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className={styles.backButton}
            >
              <FiArrowLeft size={16} /> Back to Dashboard
            </button>
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            onClick={() => setActiveTab('new')}
            className={`${styles.tabButton} ${activeTab === 'new' ? styles.activeTab : ''}`}
          >
            New
          </button>
          <button
            onClick={() => setActiveTab('preparing')}
            className={`${styles.tabButton} ${activeTab === 'preparing' ? styles.activeTab : ''}`}
          >
            Preparing
          </button>
          <button
            onClick={() => setActiveTab('ready')}
            className={`${styles.tabButton} ${activeTab === 'ready' ? styles.activeTab : ''}`}
          >
            Ready
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`${styles.tabButton} ${activeTab === 'completed' ? styles.activeTab : ''}`}
          >
            Completed
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`${styles.tabButton} ${activeTab === 'all' ? styles.activeTab : ''}`}
          >
            All Orders
          </button>
        </div>

        <div className={styles.orderContainer}>
          {/* Orders List */}
          <div className={styles.ordersList}>
            <h3 className={styles.ordersListTitle}>
              {activeTab === 'all' ? 'All Orders' : `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Orders`}
            </h3>

            {loading ? (
              <div className={styles.loadingState}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No {activeTab} orders found.</p>
              </div>
            ) : (
              <div>
                {orders.map(order => (
                  <div
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className={`${styles.orderCard} ${selectedOrder?.id === order.id ? styles.selectedOrderCard : ''}`}
                  >
                    <div className={styles.orderCardHeader}>
                      <div>
                        <strong>Order #{order.id.slice(-4)}</strong>
                        {order.table_number && <span className={styles.tableIndicator}> • Table {order.table_number}</span>}
                      </div>
                      <div className={styles.orderAmount}>${order.total_amount.toFixed(2)}</div>
                    </div>
                    <div className={styles.orderCardFooter}>
                      <div>{order.customer_name || 'Anonymous'}</div>
                      <div>{formatDate(order.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Order Details */}
          <div className={styles.orderDetails}>
            {selectedOrder ? (
              <div>
                <div className={styles.orderDetailHeader}>
                  <h3>Order Details</h3>
                  <div
                    className={styles.statusBadge}
                    style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                  >
                    {selectedOrder.status.toUpperCase()}
                  </div>
                </div>

                <div className={styles.customerInfo}>
                  <div><strong>Customer:</strong> {selectedOrder.customer_name || 'Anonymous'}</div>
                  {selectedOrder.customer_email && <div><strong>Email:</strong> {selectedOrder.customer_email}</div>}
                  {selectedOrder.table_number && <div><strong>Table:</strong> {selectedOrder.table_number}</div>}
                  <div><strong>Order Time:</strong> {formatDate(selectedOrder.created_at)}</div>
                  <div className={styles.paymentStatus}><strong>Payment Status:</strong> {selectedOrder.payment_status}</div>
                  {selectedOrder.tip_amount > 0 && (
                    <div><strong>Tip Amount:</strong> ${selectedOrder.tip_amount.toFixed(2)}</div>
                  )}
                </div>

                <div className={styles.orderItemsSection}>
                  <h4>Items:</h4>
                  <ul className={styles.orderItemsList}>
                    {selectedOrder.order_items.map(item => (
                      <li key={item.id} className={styles.orderItem}>
                        <div className={styles.orderItemHeader}>
                          <div>
                            <span className={styles.itemQuantity}>{item.quantity}x</span>
                            {item.menu_item_name}
                          </div>
                          <div>${(item.price * item.quantity).toFixed(2)}</div>
                        </div>
                        {item.special_instructions && (
                          <div className={styles.itemNotes}>
                            Note: {item.special_instructions}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={styles.orderTotal}>
                  <div>Total:</div>
                  <div>${selectedOrder.total_amount.toFixed(2)}</div>
                </div>

                {selectedOrder.status !== 'completed' && selectedOrder.status !== 'cancelled' && (
                  <div className={styles.orderActions}>
                    <h4>Update Order Status:</h4>
                    <div className={styles.actionButtons}>
                      {selectedOrder.status === 'new' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(selectedOrder.id, 'preparing')}
                            className={styles.primaryButton}
                          >
                            Move to Preparing
                          </button>
                          <button
                            onClick={() => handleStatusChange(selectedOrder.id, 'cancelled')}
                            className={styles.dangerButton}
                          >
                            Cancel Order
                          </button>
                        </>
                      )}

                      {selectedOrder.status === 'preparing' && (
                        <button
                          onClick={() => handleStatusChange(selectedOrder.id, 'ready')}
                          className={styles.successButton}
                        >
                          Mark as Ready
                        </button>
                      )}

                      {selectedOrder.status === 'ready' && (
                        <button
                          onClick={() => handleStatusChange(selectedOrder.id, 'completed')}
                          className={styles.successButton}
                        >
                          Mark as Completed
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.noOrderSelected}>
                <p>Select an order to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}